<div class="flex border-b-2 border-primary">
  <div class="flex-1 p-2">
    <h1 class="mb-2 text-2xl font-bold tracking-tight text-gray-900 text-center">Manage Branches</h1>
    <p class="font-normal text-gray-700 text-center">Search by branch to manage all associated users</p>
  </div>

  @if (selectedBranch(); as branch) {
    <div class="flex-1 flex items-center pl-2 italic">{{ branch.name }}</div>
  }
</div>

<div class="flex h-full">
  <div class="flex-1 flex flex-col h-[600px] border-r-2 border-b-2 border-primary">
    <div class="flex mx-[-1px]">
      <input
        panInput
        color="primary"
        class="w-full !rounded-none mt-[-1px]"
        placeholder="Filter {{ branches().length }} branches..."
        [(ngModel)]="branchFilter"
      />
    </div>

    <div class="flex-1 overflow-hidden overflow-y-auto">
      @if (branches().length === 0 && !hasLoadedUserBranches()) {
        <app-pan-blank-slate> <app-pan-spinner></app-pan-spinner> Loading... </app-pan-blank-slate>
      } @else if (branches().length === 0 && hasLoadedUserBranches()) {
        <app-pan-blank-slate>You do not have access to any branches.</app-pan-blank-slate>
      } @else if (visibleBranches().length === 0 && hasLoadedUserBranches()) {
        <app-pan-blank-slate>No branches match your query.</app-pan-blank-slate>
      } @else {
        <cdk-virtual-scroll-viewport itemSize="50" class="h-full">
          <div
            *cdkVirtualFor="let item of visibleBranches()"
            class="h-[50px] p-3 flex items-center border-b-2 cursor-pointer"
            [class.bg-primary-no-hover]="item === selectedBranch()"
            (click)="selectBranch(item)"
          >
            {{ item.name }}
          </div>
        </cdk-virtual-scroll-viewport>
      }
    </div>
  </div>

  <div class="flex-1 h-[600px] overflow-hidden border-r-2 border-b-2 border-primary">
    @if (visibleUsers().length > 0) {
      <div class="flex mx-[-1px] items-center relative">
        <input
          panInput
          color="primary"
          class="w-full !rounded-none mt-[-1px]"
          placeholder="Filter {{ visibleUsers().length }} users..."
          [(ngModel)]="userFilter"
        />

        <app-pan-button data-testid="add-user-button" class="absolute right-[10px]" size="sm" color="info" (click)="addUserModal.show()">Add User</app-pan-button>
      </div>
    }

    @if (!selectedBranch()) {
      <app-pan-blank-slate>Select a branch to see users for it.</app-pan-blank-slate>
    } @else if (selectedBranch() && !hasLoadedBranchUsers()) {
      <app-pan-blank-slate> <app-pan-spinner></app-pan-spinner> &nbsp;Loading... </app-pan-blank-slate>
    } @else if (selectedBranch() && hasLoadedBranchUsers() && visibleUsers().length === 0) {
      <app-pan-blank-slate>
        @if (canAddUsers()) {
          There are no users on this branch.

          <app-pan-button icon="add" color="primary" fill="outline" (click)="addUserModal.show()">Add one</app-pan-button>
        } @else {
          You don't have permissions to manage this branch.
        }
      </app-pan-blank-slate>
    } @else if (selectedBranch() && hasLoadedUserBranches() && userFilter() && visibleUsers().length === 0) {
      <app-pan-blank-slate>
        No users match your query.

        <app-pan-button icon="add" color="primary" fill="outline" (click)="addUserModal.show(); tryLoadModalUsers(userFilter())"
          >Add one</app-pan-button
        >
      </app-pan-blank-slate>
    } @else {
      <cdk-virtual-scroll-viewport [itemSize]="75" class="h-[560px]">
        <div *cdkVirtualFor="let item of visibleUsers()" class="h-[75px] p-3 flex items-center border-b-2 flex-row">
          <div class="flex-1 flex flex-col">
            <div>{{ item.name }}</div>
            <div class="italic text-sm">{{ item.email }}</div>
            <div class="italic text-xs">
              Last Login Date: {{ item.last_accessed_at ? (item.last_accessed_at | date: "mediumDate") : "Never" }}
            </div>
          </div>

          <div class="flex-1 mr-2">
            <ng-select
              appendTo="body"
              data-testid="branch-roles-selector"
              placeholder="Select roles..."
              [bindValue]="'id'"
              [bindLabel]="'name'"
              [virtualScroll]="true"
              [items]="selectableRoles()"
              [(ngModel)]="item.roles.branch[branchId()]"
              [multiple]="true"
              [clearable]="false"
              (add)="addUserRole(item, $event)"
              (remove)="removeUserRole(item, $event)"
            >
              <ng-template ng-label-tmp let-item="item" let-clear="clear">
                <span class="ng-value-label">{{ item.name }}</span>

                @if (userLogin.canGrantBranchPermission(item.id, branchId())) {
                  <span class="ng-value-icon right" (click)="clear(item)" aria-hidden="true">×</span>
                }
              </ng-template>
            </ng-select>
          </div>

          <app-pan-button color="danger" icon="delete" [swal]="removePermissionsSwal"></app-pan-button>

          <swal
            #removePermissionsSwal
            title="Remove User Permissions?"
            text="Are you sure you want to remove permissions from {{ item.name || item.email }}?"
            icon="warning"
            confirmButtonText="Yes, Remove"
            [showCancelButton]="true"
            [focusCancel]="true"
            (confirm)="removeUserPermissions(item)"
          >
          </swal>
        </div>
      </cdk-virtual-scroll-viewport>
    }
  </div>
</div>

<app-pan-modal #addUserModal (modalClose)="clearInputs()">
  <div header>Add User</div>

  <div body class="flex flex-col justify-center items-center gap-2">
    <ng-select
      class="w-full"
      placeholder="Select user..."
      [typeahead]="modalTypeaheadInput$"
      [items]="modalFoundUsers$ | async"
      [loading]="modalIsSearching()"
      [bindLabel]="'name'"
      [virtualScroll]="true"
      [clearable]="false"
      [(ngModel)]="modalUser"
      [minTermLength]="2"
      typeToSearchText="Please enter 3 or more characters"
      addTagText="Add new user"
      [addTag]="addNewUser"
    >
      <ng-template ng-option-tmp let-item="item">
        @if (item.name) {
          <p>{{ item.name }} ({{ item.email }})</p>
        } @else {
          {{ item.email }}
        }
      </ng-template>

      <ng-template ng-label-tmp let-item="item">
        @if (item.name) {
          <p>{{ item.name }} ({{ item.email }})</p>
        } @else {
          {{ item.email }}
        }
      </ng-template>
    </ng-select>

    @if (showingCreateUser() && !this.modalUser()) {
      <div class="w-full">
        <h3 class="text-lg font-bold mb-4">Create New User</h3>
        <app-pan-form-control>
          <app-pan-floating-label>New User Name</app-pan-floating-label>
          <input type="text" panInput [(ngModel)]="newUserName" placeholder="First Last" />
        </app-pan-form-control>

        <app-pan-form-control>
          <app-pan-floating-label>New User Email</app-pan-floating-label>
          <input type="text" panInput [(ngModel)]="newUserEmail" placeholder="name@domain.com" />
        </app-pan-form-control>
      </div>
    }

    <ng-select
      class="w-full"
      data-testid="modal-branch-roles-selector"
      placeholder="Select roles..."
      [bindValue]="'id'"
      [bindLabel]="'name'"
      [virtualScroll]="true"
      [items]="selectableRoles()"
      [(ngModel)]="modalRoles"
      [multiple]="true"
      [clearable]="false"
    >
      <ng-template ng-label-tmp let-item="item" let-clear="clear">
        <span class="ng-value-label">{{ item.name }}</span>

        @if (userLogin.canGrantBranchPermission(item.id, branchId())) {
          <span class="ng-value-icon right" (click)="clear(item)" aria-hidden="true">×</span>
        }
      </ng-template>
    </ng-select>
  </div>

  <div footer>
    <button panButton color="primary" (click)="submitModal()" [disabled]="!canSubmitModal()">Add User</button>
  </div>
</app-pan-modal>
